import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getCompanyInfo, updateCompanyInfo } from '../services/api';
import { toast } from 'sonner';
import { Building2, Save } from 'lucide-react';

const AdminCompany = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await getCompanyInfo();
        setCompany(res.data);
      } catch (error) {
        toast.error('Failed to load company info');
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCompanyInfo(company);
      toast.success('Company information updated successfully');
    } catch (error) {
      toast.error('Failed to update company info');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Company Information</h1>
        <p className="text-slate-600">Update your business contact details</p>
      </div>

      <Card className="p-6 bg-white border-2 border-slate-300">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Business Details</h2>
            <p className="text-sm text-slate-500">This information appears on your website</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
            <Input
              value={company?.name || ''}
              onChange={(e) => setCompany({ ...company, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
            <Input
              value={company?.phone || ''}
              onChange={(e) => setCompany({ ...company, phone: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
            <Input
              type="email"
              value={company?.email || ''}
              onChange={(e) => setCompany({ ...company, email: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Years in Business</label>
            <Input
              type="number"
              value={company?.years_in_business || ''}
              onChange={(e) => setCompany({ ...company, years_in_business: parseInt(e.target.value) })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">Business Address</label>
            <Input
              value={company?.address || ''}
              onChange={(e) => setCompany({ ...company, address: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Founded Year</label>
            <Input
              type="number"
              value={company?.founded_year || ''}
              onChange={(e) => setCompany({ ...company, founded_year: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div className="mt-6 pt-6 border-t">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminCompany;
